import React, { useMemo } from 'react';
import DataSection from './DataSection';
import styles from '../TradeFlowTrackingTab.module.css';
import {
  GenesisDecision,
  GenesisDecisionsParams,
  getGenesisDecisions,
} from '../../../services/tradeFlowAdminApi';
import { useFlowData } from '../hooks/useTradeFlowData';

interface GenesisSectionProps {
  dateFrom: string;
  dateTo: string;
  symbol: string;
}

const GENESIS_FILTER_NAMES = ['volume_24h', 'volatility_24h', 'volatility_1h', 'spread'];

const GenesisSection: React.FC<GenesisSectionProps> = ({ dateFrom, dateTo, symbol }) => {
  const [filterName, setFilterName] = React.useState('');
  const [showPassed, setShowPassed] = React.useState<string>('all');

  const params = useMemo<GenesisDecisionsParams>(() => ({
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    symbol: symbol || undefined,
    filter_name: filterName || undefined,
    passed: showPassed === 'all' ? undefined : showPassed === 'passed',
  }), [dateFrom, dateTo, symbol, filterName, showPassed]);

  const { data, total, page, setPage, isLoading, isFetching, isError, error, totalPages } =
    useFlowData<GenesisDecision, GenesisDecisionsParams>({
    queryKeyBase: ['admin', 'trade-flow', 'genesis'],
    fetchFn: (p) => getGenesisDecisions(p).then(r => r),
    params,
    refetchInterval: 10000,
  });

  return (
    <DataSection
      title="Genesis Filters"
      tabNumber={2}
      description="Volume, volatility, and spread checks that every pair must pass after being fetched from the exchange."
      loading={isLoading}
      isFetching={isFetching}
      error={error?.message || null}
      dataCount={data.length}
      page={page}
      totalPages={totalPages}
      total={total}
      onPageChange={setPage}
    >
      <div className={styles.filterBar}>
        <select
          value={filterName}
          onChange={(e) => { setFilterName(e.target.value); setPage(1); }}
          className={styles.filterSelect}
        >
          <option value="">All Filters</option>
          {GENESIS_FILTER_NAMES.map((name) => (
            <option key={name} value={name}>{name}</option>
          ))}
        </select>
        <select
          value={showPassed}
          onChange={(e) => { setShowPassed(e.target.value); setPage(1); }}
          className={styles.filterSelect}
        >
          <option value="all">All Results</option>
          <option value="passed">Passed Only</option>
          <option value="failed">Failed Only</option>
        </select>
      </div>

      <table className={styles.dataTable}>
        <thead>
          <tr>
            <th>Time</th>
            <th>Symbol</th>
            <th>Filter</th>
            <th>Result</th>
            <th>Actual Value</th>
            <th>Threshold</th>
            <th>Detail</th>
          </tr>
        </thead>
        <tbody>
          {data.map((d) => (
            <tr key={d.id}>
              <td className={styles.monoSmall}>
                {d.evaluated_at ? new Date(d.evaluated_at).toLocaleString() : '-'}
              </td>
              <td className={styles.mono}>{d.symbol}</td>
              <td>{d.filter_name}</td>
              <td>
                <span className={d.passed ? styles.badgePass : styles.badgeFail}>
                  {d.passed ? 'PASS' : 'FAIL'}
                </span>
              </td>
              <td className={styles.mono}>
                {d.actual_value !== null ? d.actual_value.toFixed(6) : '-'}
              </td>
              <td className={styles.mono}>
                {d.threshold_low !== null ? `>= ${d.threshold_low}` : ''}
                {d.threshold_high !== null ? `<= ${d.threshold_high}` : ''}
                {d.threshold_low === null && d.threshold_high === null ? '-' : ''}
              </td>
              <td className={styles.detailCell}>{d.detail || '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </DataSection>
  );
};

export default GenesisSection;
